import { 
  collection,
  addDoc,
  updateDoc,
  doc,
  serverTimestamp,
  getDoc,
  setDoc,
  query,
  where,
  getDocs,
  increment
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { UserID, User } from '../types/user';
import { getInterviewCounts } from './userService';

// Add Razorpay type definition
declare global {
  interface Window {
    Razorpay: any;
  }
}

const FUNCTIONS_BASE_URL = process.env.NODE_ENV === 'development' 
  ? 'http://localhost:6001/iiminterview-iim/europe-west1'
  : 'https://europe-west1-iiminterview-iim.cloudfunctions.net';

export interface PaymentData {
  uid: UserID;
  orderId: string;
  amount: number;
  currency: string;
  status: 'created' | 'success' | 'failed';
  plan: 'premium' | 'pro' | 'team' | 'enterprise';
  createdAt: Date;
  updatedAt: Date;
  razorpayPaymentId?: string;
  razorpayOrderId?: string;
  razorpaySignature?: string;
}

export const createPayment = async (
  uid: UserID,
  plan: 'premium' | 'team' | 'enterprise',
  amount: number
): Promise<PaymentData> => {
  try {
    // Create a payment record in Firestore
    const paymentRef = await addDoc(collection(db, 'payments'), {
      uid,
      amount,
      currency: 'INR',
      plan,
      status: 'created',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });

    // Get the created payment
    const paymentData: PaymentData = {
      uid,
      orderId: paymentRef.id,
      amount,
      currency: 'INR',
      status: 'created',
      plan,
      createdAt: new Date(),
      updatedAt: new Date()
    };

    return paymentData;
  } catch (error) {
    console.error('Error creating payment:', error);
    throw error;
  }
};

export const handlePaymentSuccess = async (
  paymentId: string,
  orderId: string,
  signature: string | undefined,
  uid: UserID,
  paymentType: 'premium' | 'top_up'
) => {
  try {
    // Update payment document
    const paymentRef = doc(db, 'payments', paymentId);
    await updateDoc(paymentRef, {
      status: 'success',
      razorpayPaymentId: orderId,
      razorpaySignature: signature,
      updatedAt: serverTimestamp()
    });

    // Get user document to update
    const userRef = doc(db, 'users', uid);
    const userDoc = await getDoc(userRef);
    
    if (!userDoc.exists()) {
      throw new Error('User document not found');
    }
    
    // Update user document based on payment type
    if (paymentType === 'premium') {
      // Premium purchase: set status and give 4 interviews
      await updateDoc(userRef, {
        subscriptionStatus: 'premium',
        has_purchased_premium: true,
        interview_quota: increment(4) // Premium users get 4 interviews
      });
      
      console.log('Premium purchase: Added 4 interviews to quota');
    } else {
      // Top-up purchase: add 4 more interviews
      if (!userDoc.data().has_purchased_premium) {
        throw new Error('Must purchase premium before top-up');
      }
      
      await updateDoc(userRef, {
        interview_quota: increment(4)
      });
      
      console.log('Top-up purchase: Added 4 interviews to quota');
    }

    return true;
  } catch (error) {
    console.error('Error handling payment success:', error);
    throw error;
  }
};

export const handlePaymentFailure = async (orderId: string) => {
  try {
    const paymentRef = doc(db, 'payments', orderId);
    await updateDoc(paymentRef, {
      status: 'failed',
      updatedAt: serverTimestamp()
    });
    return true;
  } catch (error) {
    console.error('Error handling payment failure:', error);
    throw error;
  }
};

export const initializePayment = async (
  uid: UserID,
  paymentType: 'premium' | 'top_up',
  amount: number,
  userEmail?: string,
  userName?: string
) => {
  try {
    // Log debugging information
    console.log('Initializing payment with:', {
      uid,
      paymentType,
      amount,
      userEmail,
      userName,
      razorpayKey: process.env.REACT_APP_RAZORPAY_KEY_ID
    });
    
    // Check if Razorpay is available
    if (!window.Razorpay) {
      console.error('Razorpay not found! Make sure the script is loaded.');
      alert('Payment gateway not available. Please try again later.');
      return;
    }

    // Create a new payment document with initial state
    const paymentRef = doc(collection(db, 'payments'));
    const paymentId = paymentRef.id;
    
    await setDoc(paymentRef, {
      uid,
      amount,
      paymentType,
      status: 'pending',
      createdAt: serverTimestamp()
    });

    console.log('Created payment document:', paymentId);
    
    // Razorpay options
    const options = {
      key: process.env.REACT_APP_RAZORPAY_KEY_ID,
      amount: amount,
      currency: 'INR',
      name: 'Interview Practice',
      description: paymentType === 'premium' ? 'Premium Plan (4 Interviews)' : 'Top-up (4 Interviews)',
      handler: async function(response: any) {
        try {
          console.log('Payment success response:', response);
          
          // Update payment document
          const paymentDocRef = doc(db, 'payments', paymentId);
          await updateDoc(paymentDocRef, {
            status: 'success',
            razorpayPaymentId: response.razorpay_payment_id,
            updatedAt: serverTimestamp()
          });
          
          // Update user document
          const userRef = doc(db, 'users', uid);
          const userDoc = await getDoc(userRef);
          
          if (userDoc.exists()) {
            if (paymentType === 'premium') {
              // Premium purchase - set status and give 4 interviews
              await updateDoc(userRef, {
                subscriptionStatus: 'premium',
                has_purchased_premium: true,
                interview_quota: increment(4) // Premium users get 4 interviews
              });
              
              console.log('Premium purchase completed: Added 4 interviews to quota');
            } else {
              // For top-up purchase
              if (!userDoc.data().has_purchased_premium) {
                throw new Error('Must purchase premium before top-up');
              }
              
              await updateDoc(userRef, {
                interview_quota: increment(4)
              });
              
              console.log('Top-up purchase completed: Added 4 interviews to quota');
            }
          }
          
          // Redirect to show updated interview count
          window.location.href = '/interview';
        } catch (error) {
          console.error('Error in payment success handler:', error);
          alert('Payment was successful, but there was an error updating your account. Please refresh the page.');
        }
      },
      prefill: {
        name: userName || undefined,
        email: userEmail || undefined,
      },
      theme: {
        color: '#6366f1'
      }
    };

    console.log('Razorpay options:', options);

    try {
      const rzp = new window.Razorpay(options);
      console.log('Razorpay instance created, opening payment dialog...');
      rzp.open();
      return rzp;
    } catch (rzpError) {
      console.error('Error creating Razorpay instance:', rzpError);
      alert('Error initializing payment gateway. Please try again later.');
    }

  } catch (error) {
    console.error('Error initializing payment:', error);
    throw error;
  }
}; 